import { formatMoney } from "utils/format-money";

type DeliveryConfigurationJson =
  | { id: number; type: "DIGITAL"; minCost: number }
  | {
      id: number;
      type: "PICKUP";
      minCost: number;
      authorizable: boolean;
      enableDigitalSignature: boolean;
      requiresSavedSignature: boolean;
      requiresSelectPointOfPickup: boolean;
      hasDirectExchange: boolean;
    }
  | { id: number; type: "QUENTRO"; minCost: number };

type Delivery = DeliveryConfigurationJson["type"];

abstract class DeliveryConfiguration {
  private readonly minCost: number;

  static fromJsonList(deliveries: DeliveryConfigurationJson[]) {
    const configs: DeliveryConfiguration[] = deliveries
      .filter(({ type }) => !["SHIPMENT", "STREAMING"].includes(type))
      .map(DeliveryConfiguration.fromJson);

    return configs.sort((a, b) => a.getOrder() - b.getOrder());
  }

  static fromJson(delivery: DeliveryConfigurationJson) {
    switch (delivery.type) {
      case "DIGITAL":
        return new DigitalDeliveryConfiguration(delivery.minCost);
      case "PICKUP":
        return new PickupDeliveryConfiguration(delivery.minCost, delivery.hasDirectExchange);
      case "QUENTRO":
        return new QuentroDeliveryConfiguration(delivery.minCost);
      default:
        throw new Error("Unknown delivery type");
    }
  }

  constructor(minCost: number) {
    this.minCost = minCost;
  }

  abstract getType(): Delivery;

  abstract getOrder(): number;

  getMinCost() {
    return this.minCost;
  }

  getMinCostFormatted() {
    return formatMoney(this.minCost);
  }

  isFree() {
    return this.minCost === 0;
  }

  isType(type: Delivery) {
    return this.getType() === type;
  }

  hasDirectExchange() {
    return false;
  }
}

class DigitalDeliveryConfiguration extends DeliveryConfiguration {
  getType() {
    return "DIGITAL" as const;
  }

  getOrder() {
    return 3;
  }
}

class PickupDeliveryConfiguration extends DeliveryConfiguration {
  private readonly directExchange: boolean;

  constructor(minCost: number, hasDirectExchange: boolean) {
    super(minCost);
    this.directExchange = hasDirectExchange;
  }

  getType() {
    return "PICKUP" as const;
  }

  getOrder() {
    return 1;
  }

  hasDirectExchange() {
    return this.directExchange;
  }
}

class QuentroDeliveryConfiguration extends DeliveryConfiguration {
  getType() {
    return "QUENTRO" as const;
  }

  getOrder() {
    return 2;
  }
}

export type { DeliveryConfigurationJson, Delivery };
export { DeliveryConfiguration };
